import instrumentSchema from '../schemas/instrument-schema.json';
import type {
  ContextualValveType,
  InstrumentCoreFieldValue,
  InstrumentCoreFields,
  PnidInstrument,
  PnidInstrumentUpdate,
} from '../types';
import { canonicalizeInstrumentType, CONTEXTUAL_TYPE_GROUPS } from '../types';
import {
  getSubtypesForAnyType,
  getUnifiedInstrumentTypeOptions,
  isContextualTypeApplicable,
  isIsolationValveApplicable,
} from './instrumentTypes';

export type CoreFieldWidget = 'text' | 'textarea' | 'select' | 'multiselect' | 'checkbox';
export type CoreFieldValueType = 'string' | 'string_array' | 'boolean';
export type CoreFieldConditionSource = 'shared' | 'core' | 'derived';
export type CoreFieldConditionOperator = 'equals' | 'not_equals' | 'in' | 'not_in';
export type CoreFieldConditionValue = boolean | string | null | string[];

export interface CoreFieldCondition {
  source: CoreFieldConditionSource;
  field: string;
  operator: CoreFieldConditionOperator;
  value: CoreFieldConditionValue;
}

export interface CoreFieldVisibility {
  all?: CoreFieldCondition[];
  any?: CoreFieldCondition[];
}

export interface CoreFieldDefinition {
  id: string;
  storage_key: string;
  widget: CoreFieldWidget;
  label: string;
  /** A label that replaces `label` for the named instrument type. */
  label_by_instrument_type?: Record<string, string>;
  placeholder: string;
  allow_custom: boolean;
  default_value: string | string[] | boolean;
  empty_value: string | string[] | boolean | null;
  options_source: string | null;
  options_set?: string | null;
  visible_when?: CoreFieldVisibility;
  clear_when_hidden: boolean;
  value_type: CoreFieldValueType;
}

export interface CoreFieldOption {
  value: string;
  label: string;
}

/** Same-doc tag candidates for fields whose options depend on another core field
 * (e.g. interlock acting_on_ref lists equipment/instrument tags per acting_on_type). */
export interface ActingOnCandidates {
  equipment: string[];
  instrument: string[];
}

export interface SharedInstrumentEditorState {
  mode: 'instrument' | 'branch_join';
  context_type: 'equipment' | 'node' | 'instrument' | null;
  context_id: string | null;
  branch_ref: string;
  branch_direction: string;
  present_on_node: boolean;
  /** The plant domain that owns the instrument. Empty means the drawing's own. */
  owner_domain_id: string;
  /** The instrument's description as typed. Empty means none. */
  description: string;
}

type DerivedFieldFlags = {
  subtype_options_available: boolean;
  contextual_type_applicable: boolean;
  isolation_valve_applicable: boolean;
};

type CoreFormOptionSets = Record<string, CoreFieldOption[]>;

const CORE_FORM = instrumentSchema.core_form as {
  fields: CoreFieldDefinition[];
  known_backend_mirrors: Record<string, string>;
  option_sets?: CoreFormOptionSets;
};

export const CORE_FORM_FIELDS = CORE_FORM.fields;
export const KNOWN_BACKEND_MIRRORS = CORE_FORM.known_backend_mirrors;
const CORE_FORM_OPTION_SETS: CoreFormOptionSets = CORE_FORM.option_sets ?? {};

function getDefaultCoreFieldValue(field: CoreFieldDefinition): InstrumentCoreFieldValue {
  if (field.value_type === 'string_array') {
    return Array.isArray(field.default_value) ? [...field.default_value] : [];
  }
  if (field.value_type === 'boolean') {
    return typeof field.default_value === 'boolean' ? field.default_value : false;
  }
  return typeof field.default_value === 'string' ? field.default_value : '';
}

function getEmptyCoreFieldValue(field: CoreFieldDefinition): InstrumentCoreFieldValue {
  if (field.value_type === 'string_array') {
    return Array.isArray(field.empty_value) ? [...field.empty_value] : [];
  }
  if (field.value_type === 'boolean') {
    return typeof field.empty_value === 'boolean' ? field.empty_value : false;
  }
  if (typeof field.empty_value === 'string') {
    return field.empty_value;
  }
  return null;
}

function normalizeIncomingValue(field: CoreFieldDefinition, value: unknown): InstrumentCoreFieldValue {
  if (field.value_type === 'string_array') {
    if (Array.isArray(value)) {
      return value.map((entry) => String(entry));
    }
    if (typeof value === 'string' && value) {
      return [value];
    }
    return getDefaultCoreFieldValue(field);
  }
  if (field.value_type === 'boolean') {
    if (typeof value === 'boolean') {
      return value;
    }
    return getDefaultCoreFieldValue(field);
  }

  if (value === null || value === undefined) {
    return getDefaultCoreFieldValue(field);
  }

  return String(value);
}

function serializeCoreValue(field: CoreFieldDefinition, value: InstrumentCoreFieldValue): InstrumentCoreFieldValue {
  if (field.value_type === 'string_array') {
    const normalized = Array.isArray(value)
      ? value.map((entry) => String(entry)).filter(Boolean)
      : [];
    return normalized.length > 0 ? normalized : getEmptyCoreFieldValue(field);
  }
  if (field.value_type === 'boolean') {
    return typeof value === 'boolean' ? value : getEmptyCoreFieldValue(field);
  }

  const stringValue = typeof value === 'string' ? value : '';
  return stringValue === '' ? getEmptyCoreFieldValue(field) : stringValue;
}

function readMirroredValue(instrument: PnidInstrument, mirrorKey: string): unknown {
  return (instrument as unknown as Record<string, unknown>)[mirrorKey];
}

function getDerivedFlags(
  shared: SharedInstrumentEditorState,
  core: InstrumentCoreFields,
): DerivedFieldFlags {
  const instrumentType = typeof core.instrument_type === 'string' ? core.instrument_type : '';
  const subtypeOptions = getSubtypesForAnyType(instrumentType);

  return {
    subtype_options_available: subtypeOptions.length > 0,
    contextual_type_applicable: isContextualTypeApplicable(instrumentType, shared.context_type),
    isolation_valve_applicable: isIsolationValveApplicable(instrumentType),
  };
}

function resolveConditionValue(
  condition: CoreFieldCondition,
  shared: SharedInstrumentEditorState,
  core: InstrumentCoreFields,
  derived: DerivedFieldFlags,
): InstrumentCoreFieldValue | boolean | string | null | undefined {
  if (condition.source === 'shared') {
    return (shared as unknown as Record<string, unknown>)[condition.field] as string | boolean | null | undefined;
  }
  if (condition.source === 'core') {
    return core[condition.field];
  }
  return (derived as Record<string, unknown>)[condition.field] as boolean | undefined;
}

function evaluateCondition(
  condition: CoreFieldCondition,
  shared: SharedInstrumentEditorState,
  core: InstrumentCoreFields,
  derived: DerivedFieldFlags,
): boolean {
  const actual = resolveConditionValue(condition, shared, core, derived);
  if (condition.operator === 'in' || condition.operator === 'not_in') {
    const allowedValues = Array.isArray(condition.value) ? condition.value : [];
    const matches = typeof actual === 'string' ? allowedValues.includes(actual) : false;
    return condition.operator === 'in' ? matches : !matches;
  }
  if (condition.operator === 'not_equals') {
    return actual !== condition.value;
  }
  return actual === condition.value;
}

export function getCoreFormState(instrument: PnidInstrument): InstrumentCoreFields {
  const coreState: InstrumentCoreFields = {};
  const persistedCore = instrument.core_fields || {};
  const instrumentType = canonicalizeInstrumentType(
    typeof persistedCore.instrument_type === 'string'
      ? persistedCore.instrument_type
      : (instrument.instrument_type ?? ''),
  );
  const hasLegacyManualHandwheel =
    instrumentType === 'Control Valve' && persistedCore.actuator_type === 'manual handwheel';
  const hasLegacyControlValveTso =
    instrumentType === 'Control Valve' && persistedCore.fail_safe_position === 'TSO';
  const hasLegacyControlValvePositioner =
    instrumentType === 'Control Valve' && persistedCore.actuator_type === 'With positioner';

  for (const field of CORE_FORM_FIELDS) {
    const fallbackKey = KNOWN_BACKEND_MIRRORS[field.id];
    let rawValue = persistedCore[field.storage_key] ?? (fallbackKey ? readMirroredValue(instrument, fallbackKey) : undefined);

    if (field.storage_key === 'instrument_type' && typeof rawValue === 'string') {
      rawValue = canonicalizeInstrumentType(rawValue);
    }

    if (field.storage_key === 'actuator_type' && rawValue === 'manual handwheel') {
      rawValue = undefined;
    }
    if (field.storage_key === 'actuator_type' && rawValue === 'With positioner') {
      rawValue = undefined;
    }
    if (field.storage_key === 'handwheel' && rawValue === undefined && hasLegacyManualHandwheel) {
      rawValue = true;
    }
    if (field.storage_key === 'positioner' && rawValue === undefined && hasLegacyControlValvePositioner) {
      rawValue = true;
    }
    if (field.storage_key === 'fail_safe_position' && rawValue === 'TSO') {
      rawValue = undefined;
    }
    if (field.storage_key === 'tso' && rawValue === undefined && hasLegacyControlValveTso) {
      rawValue = true;
    }

    coreState[field.storage_key] = normalizeIncomingValue(field, rawValue);
  }

  return coreState;
}

export function buildCoreFieldOptions(
  field: CoreFieldDefinition,
  shared: SharedInstrumentEditorState,
  core: InstrumentCoreFields,
  allInstrumentTexts: string[],
  actingOnCandidates?: ActingOnCandidates,
): CoreFieldOption[] {
  if (field.options_set) {
    return CORE_FORM_OPTION_SETS[field.options_set] ?? [];
  }

  void shared;
  switch (field.options_source) {
    case 'unified_instrument_types':
      return getUnifiedInstrumentTypeOptions();
    case 'subtypes_for_selected_instrument_type': {
      const instrumentType = typeof core.instrument_type === 'string' ? core.instrument_type : '';
      return getSubtypesForAnyType(instrumentType).map((value) => ({ value, label: value }));
    }
    case 'contextual_types_grouped':
      return Object.entries(CONTEXTUAL_TYPE_GROUPS).flatMap(([groupName, types]) =>
        Object.entries(types).map(([code, name]) => ({
          value: code,
          label: `${code} — ${name} (${groupName})`,
        }))
      );
    case 'instrument_text_candidates': {
      const uniqueTexts = Array.from(new Set(allInstrumentTexts.filter(Boolean)));
      return uniqueTexts.map((value) => ({ value, label: value }));
    }
    case 'acting_on_candidates': {
      const actingOnType = typeof core.acting_on_type === 'string' ? core.acting_on_type : '';
      if (!actingOnCandidates || !actingOnType) {
        return [];
      }
      const candidates =
        actingOnType === 'equipment'
          ? actingOnCandidates.equipment
          : actingOnType === 'instrument'
            ? actingOnCandidates.instrument
            : [];
      return Array.from(new Set(candidates.filter(Boolean))).map((value) => ({ value, label: value }));
    }
    default:
      return [];
  }
}

/** The field's label for the selected type: the schema's per-type override, else its label. */
export function coreFieldLabel(
  field: Pick<CoreFieldDefinition, 'label' | 'label_by_instrument_type'>,
  instrumentType: string | null | undefined,
): string {
  const canonicalType = canonicalizeInstrumentType(instrumentType);
  return (canonicalType && field.label_by_instrument_type?.[canonicalType]) || field.label;
}

export function isCoreFieldVisible(
  field: CoreFieldDefinition,
  shared: SharedInstrumentEditorState,
  core: InstrumentCoreFields,
): boolean {
  if (!field.visible_when) {
    return true;
  }

  const derived = getDerivedFlags(shared, core);
  if (field.visible_when.all && !field.visible_when.all.every((condition) => evaluateCondition(condition, shared, core, derived))) {
    return false;
  }
  if (field.visible_when.any && !field.visible_when.any.some((condition) => evaluateCondition(condition, shared, core, derived))) {
    return false;
  }
  return true;
}

/**
 * Clears what the form can no longer show or offer: a hidden field the schema marks
 * for clearing, a select whose value has left its option list, and a hidden field
 * holding a value the SME never entered.
 *
 * That last rule is why `touchedFields` is here. A field the schema keeps through
 * hiding keeps it for the SME's sake — so a mode toggled back and forth does not
 * lose the tag they typed. A machine suggestion has no such claim: left in a hidden
 * field it is saved, and validated, without the SME ever having seen it.
 */
export function sanitizeCoreFormState(
  core: InstrumentCoreFields,
  shared: SharedInstrumentEditorState,
  allInstrumentTexts: string[],
  actingOnCandidates?: ActingOnCandidates,
  touchedFields: TouchedCoreFields = new Set<string>(),
): InstrumentCoreFields {
  const next: InstrumentCoreFields = { ...core };

  for (const field of CORE_FORM_FIELDS) {
    const currentValue = next[field.storage_key];
    const isVisible = isCoreFieldVisible(field, shared, next);
    if (!isVisible) {
      if (field.clear_when_hidden || !touchedFields.has(field.storage_key)) {
        next[field.storage_key] = getDefaultCoreFieldValue(field);
      }
      continue;
    }

    if (
      field.value_type === 'string' &&
      (field.options_source === 'subtypes_for_selected_instrument_type' ||
        field.options_source === 'acting_on_candidates' ||
        field.options_set) &&
      typeof currentValue === 'string' &&
      currentValue
    ) {
      const options = buildCoreFieldOptions(field, shared, next, allInstrumentTexts, actingOnCandidates);
      if (options.length > 0 && !options.some((option) => option.value === currentValue)) {
        next[field.storage_key] = getDefaultCoreFieldValue(field);
      }
    }
  }

  return next;
}

/** Storage keys the SME has edited by hand since the editor opened. */
export type TouchedCoreFields = ReadonlySet<string>;

/** True when the field holds no value a reader would see. */
export function isCoreFieldEmpty(
  field: CoreFieldDefinition,
  value: InstrumentCoreFieldValue | undefined,
): boolean {
  if (field.value_type === 'string_array') {
    return !Array.isArray(value) || value.length === 0;
  }
  if (field.value_type === 'boolean') {
    return value !== true;
  }
  return value === null || value === undefined || value === '';
}

/**
 * What the identify answer says this field is, in the form's own value type. A key
 * the answer omits comes back as the field's blank value, because the answer is the
 * whole suggestion and silence in it means "no value", not "unchanged".
 */
function identifiedCoreFieldValue(
  field: CoreFieldDefinition,
  identifiedFields: Record<string, unknown>,
): InstrumentCoreFieldValue {
  const identified = identifiedFields[field.storage_key];
  if (field.storage_key === 'instrument_type' && typeof identified === 'string') {
    return canonicalizeInstrumentType(identified);
  }
  return normalizeIncomingValue(field, identified);
}

/**
 * Writes identified values into the blank fields of `core` and leaves every other
 * field as it stands. This is the shape a first answer takes, where the form holds
 * nothing the SME or the record has put there and nothing can be lost.
 */
export function fillEmptyCoreFields(
  core: InstrumentCoreFields,
  identifiedFields: Record<string, unknown>,
): InstrumentCoreFields {
  const filled: InstrumentCoreFields = { ...core };

  for (const field of CORE_FORM_FIELDS) {
    if (!isCoreFieldEmpty(field, filled[field.storage_key])) {
      continue;
    }
    const identified = identifiedCoreFieldValue(field, identifiedFields);
    if (isCoreFieldEmpty(field, identified)) {
      continue;
    }
    filled[field.storage_key] = identified;
  }

  return filled;
}

/**
 * Sets every core field from the answer, blanks included.
 *
 * A later answer describes a different asset from the earlier one — the SME retyped
 * the tag, which is the input every rule reads — so nothing the earlier answer said
 * may survive under the new tag, and silence in the new answer clears.
 *
 * Precedence has one owner, and it is the producer: the request carries the keys the
 * SME edited, and the answer already has their values in it. The client deciding
 * again here is how the two sides come to disagree, so it does not decide.
 */
export function applyIdentifiedCoreFields(
  core: InstrumentCoreFields,
  identifiedFields: Record<string, unknown>,
): InstrumentCoreFields {
  const next: InstrumentCoreFields = { ...core };

  for (const field of CORE_FORM_FIELDS) {
    next[field.storage_key] = identifiedCoreFieldValue(field, identifiedFields);
  }

  return next;
}

export function buildCoreInstrumentUpdates(core: InstrumentCoreFields): PnidInstrumentUpdate {
  const updates: PnidInstrumentUpdate = {};
  const serializedCoreFields: Record<string, InstrumentCoreFieldValue> = {};

  for (const field of CORE_FORM_FIELDS) {
    const serialized = serializeCoreValue(field, core[field.storage_key]);
    serializedCoreFields[field.storage_key] = serialized;

    const mirrorKey = KNOWN_BACKEND_MIRRORS[field.id];
    if (!mirrorKey) {
      continue;
    }

    const record = updates as Record<string, unknown>;
    if (mirrorKey === 'contextual_type') {
      record[mirrorKey] = (serialized as string | null) as ContextualValveType | null;
    } else {
      record[mirrorKey] = serialized;
    }
  }

  updates.core_fields = serializedCoreFields;
  return updates;
}

export function haveCoreFormValuesChanged(original: PnidInstrument, core: InstrumentCoreFields): boolean {
  return JSON.stringify(getCoreFormState(original)) !== JSON.stringify(core);
}
